import { useEffect, useState } from 'react';

import { ListItemText, Menu, MenuItem } from '@mui/material';

import FilterButton from './FilterButton';
import { useFilterAnchor } from './useFilterAnchor';
import { NO_ALGORITHM_APPLIED_FILTER_VALUE } from '../../common';
import AlgorithmChip from '../cells/AlgorithmChip';

import type { AlgorithmIdFilterValue } from '../../schemas';

import { useGetAlgorithmsQuery } from '@/api/endpoints/trafficking';
import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';
import { Checkbox } from '@/shared/components/checkbox';

interface AlgorithmsFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  searchParamAlgorithmIds: AlgorithmIdFilterValue[] | undefined;
  handleNavigation: (
    selectedAlgoIds: AlgorithmIdFilterValue[] | undefined
  ) => void;
}

const AlgorithmsFilter = ({
  filterId,
  buttonAriaLabel,
  searchParamAlgorithmIds,
  handleNavigation,
}: AlgorithmsFilterProps) => {
  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const { data: algorithms = [], isLoading } = useGetAlgorithmsQuery(null);

  const [selectedAlgos, setSelectedAlgos] = useState<
    AlgorithmIdFilterValue[] | undefined
  >(searchParamAlgorithmIds);
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  // validate algorithm ids present in searchParamAlgorithmIds with the fetched algorithms
  // if invalid ids are found, update selectedAlgos to only include valid ids
  useEffect(() => {
    if (!searchParamAlgorithmIds?.length || !algorithms.length) {
      return;
    }

    const validAlgorithmIds = searchParamAlgorithmIds.filter(
      (id) =>
        id === NO_ALGORITHM_APPLIED_FILTER_VALUE ||
        algorithms.some((algo) => algo.id === id)
    );

    if (validAlgorithmIds.length !== searchParamAlgorithmIds.length) {
      setSelectedAlgos(validAlgorithmIds);
    }
  }, [algorithms, searchParamAlgorithmIds]);

  const handleAlgorithmChange = (algorithmId: AlgorithmIdFilterValue) => {
    setIsChangedInternally(true);

    setSelectedAlgos((prev) => {
      const algosArray = prev ?? [];
      const nextAlgos = algosArray.includes(algorithmId)
        ? algosArray.filter((v) => v !== algorithmId)
        : [...algosArray, algorithmId];

      return nextAlgos.length > 0 ? nextAlgos : undefined;
    });
  };

  const handleSelectAll = () => {
    setIsChangedInternally(true);

    const allAlgorithmIds = algorithms.map((algo) => algo.id);
    const isAllSelected = allAlgorithmIds.every((id) =>
      selectedAlgos?.includes(id)
    );

    if (isAllSelected) {
      setSelectedAlgos(undefined);
    } else {
      setSelectedAlgos(allAlgorithmIds);
    }
  };

  const isAllSelected =
    algorithms.length > 0 &&
    algorithms.every((algo) => selectedAlgos?.includes(algo.id));

  useEffect(() => {
    setSelectedAlgos(searchParamAlgorithmIds);
    setIsChangedInternally(false);
  }, [searchParamAlgorithmIds]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(selectedAlgos);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    handleNavigation,
    isChangedInternally,
    selectedAlgos,
    searchParamAlgorithmIds,
  ]);

  const selectAllItem = algorithms.length ? (
    <MenuItem
      key='select-all'
      onClick={handleSelectAll}
      disableGutters
      sx={{
        py: 1.25,
        pr: 1.75,
        pl: 1,
      }}
    >
      <Checkbox
        id='select-all-algorithms'
        checked={isAllSelected}
        indeterminate={!isAllSelected && (selectedAlgos?.length ?? 0) > 0}
      />
      <ListItemText
        primary='(Select All)'
        slotProps={{
          primary: { variant: 'body3', color: 'textTertiary' },
        }}
      />
    </MenuItem>
  ) : null;

  const noAlgorithmItem = (
    <MenuItem
      key='no-algorithm-applied'
      onClick={() => {
        handleAlgorithmChange(NO_ALGORITHM_APPLIED_FILTER_VALUE);
      }}
      disableGutters
      sx={{
        py: 1.25,
        pr: 1.75,
        pl: 1,
      }}
    >
      <Checkbox
        id='no-algorithm-applied'
        checked={
          selectedAlgos?.includes(NO_ALGORITHM_APPLIED_FILTER_VALUE) ?? false
        }
      />
      <ListItemText
        primary='No Algorithm Applied'
        slotProps={{ primary: { variant: 'body2' } }}
      />
    </MenuItem>
  );

  const algorithmItems = algorithms.map((algorithm) => (
    <MenuItem
      key={algorithm.id}
      onClick={() => {
        handleAlgorithmChange(algorithm.id);
      }}
      disableGutters
      sx={{
        py: 1.25,
        pr: 1.75,
        pl: 1,
      }}
    >
      <Checkbox
        id={algorithm.id.toString()}
        checked={selectedAlgos?.includes(algorithm.id) ?? false}
      />

      <AlgorithmChip
        size='small'
        label={`${algorithm.name} ${algorithm.version ? `(${algorithm.version})` : ''}`}
        textColor={algorithm.color}
        sx={{
          fontSize: '0.75rem',
          lineHeight: '1.125rem',
        }}
      />
    </MenuItem>
  ));

  return (
    <>
      <FilterButton
        isActive={!!selectedAlgos?.length}
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
        loading={isLoading}
      />
      <Menu
        id={popoverId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {selectAllItem}
        {noAlgorithmItem}
        {algorithmItems}
      </Menu>
    </>
  );
};

export default AlgorithmsFilter;
